import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../lib/api-client';
import type {
  CreateOutreachLogInput,
  QuickWhatsAppInput,
  SendReminderEmailInput,
  OutreachStatus,
} from '@lms/shared/types';

export const crmKeys = {
  all: ['crm'] as const,
  participants: (params: Record<string, unknown>) =>
    [...crmKeys.all, 'participants', params] as const,
  history: (userId: string) => [...crmKeys.all, 'history', userId] as const,
};

export function useCrmParticipants(params?: {
  inactivityType?: string;
  outreachStatus?: string;
  programId?: string;
  gender?: string;
  search?: string;
  page?: number;
  limit?: number;
}) {
  return useQuery({
    queryKey: crmKeys.participants(params || {}),
    queryFn: () => apiClient.crm.getParticipants(params),
  });
}

export function useCrmOutreachHistory(userId: string) {
  return useQuery({
    queryKey: crmKeys.history(userId),
    queryFn: () => apiClient.crm.getOutreachHistory(userId),
    enabled: !!userId,
  });
}

export function useCreateOutreachLog() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateOutreachLogInput) => apiClient.crm.createOutreachLog(data),
    onSuccess: data => {
      void queryClient.invalidateQueries({ queryKey: crmKeys.all });
      if (data.userId) {
        void queryClient.invalidateQueries({
          queryKey: crmKeys.history(data.userId),
        });
      }
    },
  });
}

export function useSendQuickWhatsApp() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: QuickWhatsAppInput) => apiClient.crm.sendQuickWhatsApp(data),
    onSuccess: data => {
      void queryClient.invalidateQueries({ queryKey: crmKeys.all });
      if (data.outreachLog?.userId) {
        void queryClient.invalidateQueries({
          queryKey: crmKeys.history(data.outreachLog.userId),
        });
      }
    },
  });
}

export function useSendReminderEmail() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: SendReminderEmailInput) => apiClient.crm.sendReminderEmail(data),
    onSuccess: data => {
      void queryClient.invalidateQueries({ queryKey: crmKeys.all });
      if (data.outreachLog?.userId) {
        void queryClient.invalidateQueries({
          queryKey: crmKeys.history(data.outreachLog.userId),
        });
      }
    },
  });
}

export function useUpdateOutreachLog() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...data }: { id: string; status?: OutreachStatus; notes?: string }) =>
      apiClient.crm.updateOutreachLog(id, data),
    onSuccess: data => {
      void queryClient.invalidateQueries({ queryKey: crmKeys.all });
      if (data.userId) {
        void queryClient.invalidateQueries({
          queryKey: crmKeys.history(data.userId),
        });
      }
    },
  });
}
